import React, { useEffect, useState } from 'react';
import { theme } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import { DependencyTree } from '../DependencyTree';
import Helpers from 'utils/Helpers';
import { gapsAuditLevels } from 'constants/gapsAuditLevels';
import { renderGapNode } from './renderNode';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../Layouts/Layout.styled';
import * as LocalStyled from 'pages/Reporting/ControlReport/ControlReport.styled';

const { useToken } = theme;
const GapsAuditSolutionsReport = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);

  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const [reportData, setReportData] = useState<any[]>([]);
  // report api

  useEffect(() => {
    const filterParams = Helpers.toApiFilters(orgContext);
    const normalizedFilters = Helpers.normalizeFilters(
      Helpers.toApiFilters(orgContext)
    );

    const fetchReport = async () => {
      try {
        if (!orgContext || Object.keys(orgContext).length === 0) return;
        const hasFilters = Object.keys(filterParams).length > 0;
        if (!hasFilters) return;
        const response = await ApiService.getBruGapsAuditsSolutions(
          lang,
          normalizedFilters
        );

        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        const formattedData = responseData.flatMap((item: any) => {
          const gaps = Object.values(item.GAPS || {});
          const solutions = Object.values(item.SOLUTIONS || {});
          const audits = Object.values(item.AUDITS || {});

          return gaps.flatMap((gap: any) => {
            const gapSolutions = solutions.map((solution: any) => ({
              // hierarchy keys
              bru_key_value: item.bru_key_value,

              gap_id: gap.gap_id,
              gap_description: gap.gap_description,

              sol_id: solution.sol_id,
              sol_description: solution.sol_description,

              // gap info
              gap_due_date: gap.gap_due_date,
              gap_closed: gap.gap_closed,
              priority_name: gap.gap_priority_name,
              owner_name: gap.gap_owner_name,

              // solution info
              sol_approved: solution.sol_approved,
              sol_approver_name: solution.sol_approver_name,
              sol_approver_notified: solution.sol_approver_notified,
              sol_evd_required: solution.sol_evd_required,
              sol_reviewed: solution.sol_reviewed,
              sol_reviewer_name: solution.sol_reviewer_name,
              sol_reviewer_notified: solution.sol_reviewer_notified,
              sol_type_name: solution.sol_type_name,

              // audits if exist
              afg_description: audits.map((a: any) => a.afg_description)
            }));

            return gapSolutions;
          });
        });

        setReportData(formattedData);
      } catch (error) {
        console.error(error);
        setReportData([]);
      }
    };

    fetchReport();
  }, []);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={t('BRU_GAPS_AUDITS_SOLUTIONS')}
        goBackButtons={<GoBackButton to='/dependencies' text={t('GO_BACK')} />}
      />
      <Styled.HorizontalLayoutMain theme={scheme}>
        <Styled.Container>
          <Styled.ContainerDependency token={token}>
            <Styled.CardItemWrapper>
              {reportData.length === 0 ? (
                <LocalStyled.EmptyState>
                  {t('common.noData')}
                </LocalStyled.EmptyState>
              ) : (
                <DependencyTree
                  data={reportData}
                  levels={gapsAuditLevels}
                  renderNode={renderGapNode}
                />
              )}
            </Styled.CardItemWrapper>
          </Styled.ContainerDependency>
        </Styled.Container>
      </Styled.HorizontalLayoutMain>
    </Styled.StyledLayout>
  );
};

export default GapsAuditSolutionsReport;
